/* eslint-disable consistent-return, default-case, no-param-reassign */
/*
 *
 * Amortization reducer
 *
 */

import produce from 'immer';
import isEmpty from 'lodash/isEmpty';

import {
  AMORTIZE_CALCULATE,
  AMORTIZE_CALCULATE_FAILURE,
  AMORTIZE_CALCULATE_SUCCESS,
  AMORTIZE_STATUS,
  AMORTIZE_STATUS_FAILURE,
  AMORTIZE_STATUS_SUCCESS,
  CHANGE_ADJUSTMENT_FILTER,
  CHANGE_INPUT,
  CHANGE_STEP,
  RESET_ALL_CALCULATORS,
  RESET_CALCULATOR,
  RESET_CALCULATOR_RESULTS,
  RESET_FORM_ERROR,
  RESET_STATE,
  RESET_STATUS,
  RESET_STEP,
  SET_IS_SCHEDULE_DRAWER_OPEN,
  SET_IS_SLIDER_FOCUSED,
  UPDATE_FORM_ERROR,
} from './constants';

export const initialState = {
  currentPaymentsData: {
    cumulativeInterestAmount: '',
    paymentDate: '',
  },
  demoSite: {
    prePopulatedAmortizationFields: null,
    shouldPrePopulateAmortizeFields: false,
  },
  errors: {
    bannerError: false,
    serviceError: {
      calculate: false,
      fetchStatus: false,
    },
  },
  formErrors: {
    amortization: {
      amount: '',
      interestRate: '',
      monthlyPaymentAmount: '',
      numberOfPayments: '',
      principalBalance: '',
      startDate: '',
    },
    payLumpSum: { amount: '' },
    payMoreMonthly: { amount: '' },
  },
  forms: {
    amortization: {
      amount: '',
      interestRate: '',
      monthlyPaymentAmount: '',
      numberOfPayments: '',
      paymentType: 'Optional',
      principalBalance: '',
      startDate: '',
      valueType: '',
    },
    payLumpSum: { amount: '0.00', resultAmount: '0' },
    payMoreMonthly: { amount: '0.00', resultAmount: '0' },
  },
  isScheduleDrawerOpen: false,
  isSliderFocused: {
    payLumpSum: false,
    payMoreMonthly: false,
  },
  selectedAdjustmentFilter: 'amortization',
  shortenLoanBy: [],
  status: {
    amortization: 'idle',
    fetchStatus: 'idle',
    payLumpSum: 'idle',
    payMoreMonthly: 'idle',
  },
  statusData: {
    currentSetting: null,
    defaultPayments: null,
    newPayments: null,
    payments: {
      payLumpSum: null,
      payMoreMonthly: null,
    },
  },
  step: 1,
};

const amortizationReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case AMORTIZE_CALCULATE: {
      const { amount, calculateStatus } = payload;
      draft.errors = initialState.errors;
      draft.status[calculateStatus] = 'loading';
      draft.forms[calculateStatus].resultAmount = amount;
      break;
    }
    case AMORTIZE_CALCULATE_FAILURE: {
      const { calculateStatus, error } = payload;
      const { validationErrors } = error;
      if (!isEmpty(validationErrors)) {
        Object.keys(validationErrors).forEach((input) => {
          draft.formErrors[calculateStatus][input] =
            validationErrors[input].message;
        });
      } else {
        draft.errors.serviceError.calculate = error;
      }
      draft.status[calculateStatus] = 'failed';
      break;
    }
    case AMORTIZE_CALCULATE_SUCCESS: {
      const { calculateStatus, currentSetting, payments } = payload;
      const { additionalPayment } = currentSetting;
      if (calculateStatus === 'amortization') {
        draft.statusData.newPayments = payments;
      } else if (additionalPayment) {
        const { paymentType } = additionalPayment;
        if (paymentType === 'Monthly') {
          draft.statusData.payments.payMoreMonthly = payments;
        }
        if (paymentType === 'One_Time') {
          draft.statusData.payments.payLumpSum = payments;
        }
      }
      draft.status[calculateStatus] = 'succeeded';
      break;
    }
    case AMORTIZE_STATUS: {
      draft.errors = initialState.errors;
      draft.status.fetchStatus = 'loading';
      break;
    }
    case AMORTIZE_STATUS_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.fetchStatus = error;
      draft.status.fetchStatus = 'failed';
      break;
    }
    case AMORTIZE_STATUS_SUCCESS: {
      const {
        currentSetting,
        payments,
        prePopulatedAmortizationFields,
      } = payload;
      draft.status.fetchStatus = 'succeeded';
      draft.statusData.currentSetting = currentSetting;
      draft.statusData.defaultPayments = payments;
      if (payments.length) {
        const { cumulativeInterestAmount, paymentDate } = payments[payments.length - 1];
        draft.currentPaymentsData.cumulativeInterestAmount = cumulativeInterestAmount;
        draft.currentPaymentsData.paymentDate = paymentDate;
      }
      if (prePopulatedAmortizationFields) {
        draft.demoSite.shouldPrePopulateAmortizeFields = true;
        draft.demoSite.prePopulatedAmortizationFields = prePopulatedAmortizationFields;
        draft.forms.amortization = { ...prePopulatedAmortizationFields };
      } else {
        draft.demoSite = initialState.demoSite;
        draft.forms.amortization = initialState.forms.amortization;
      }
      break;
    }
    case CHANGE_ADJUSTMENT_FILTER: {
      const { filter } = payload;
      draft.formErrors[filter] = initialState.formErrors[filter];
      draft.selectedAdjustmentFilter = filter;
      break;
    }
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.forms[form][field] = value;
      draft.formErrors[form][field] = initialState.formErrors[form][field];
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case RESET_ALL_CALCULATORS: {
      draft.errors = initialState.errors;
      draft.formErrors = initialState.formErrors;
      draft.forms.payLumpSum = initialState.forms.payLumpSum;
      draft.forms.payMoreMonthly = initialState.forms.payMoreMonthly;
      if (draft.demoSite.prePopulatedAmortizationFields) {
        draft.forms.amortization = { ...draft.demoSite.prePopulatedAmortizationFields };
      } else {
        draft.forms.amortization = initialState.forms.amortization;
      }
      draft.selectedAdjustmentFilter = initialState.selectedAdjustmentFilter;
      draft.status.amortization = initialState.status.amortization;
      draft.status.payLumpSum = initialState.status.payLumpSum;
      draft.status.payMoreMonthly = initialState.status.payMoreMonthly;
      draft.statusData.newPayments = initialState.statusData.newPayments;
      draft.statusData.payments = initialState.statusData.payments;
      break;
    }
    case RESET_CALCULATOR: {
      const { form } = payload;
      draft.formErrors[form] = initialState.formErrors[form];
      draft.forms[form] = initialState.forms[form];
      draft.status[form] = initialState.status[form];
      draft.statusData.payments[form] = initialState.statusData.payments[form];
      break;
    }
    case RESET_CALCULATOR_RESULTS: {
      const { calculateStatus } = payload;
      draft.status[calculateStatus] = initialState.status[calculateStatus];
      draft.statusData.newPayments = initialState.statusData.newPayments;
      draft.forms[calculateStatus].resultAmount = initialState.forms[calculateStatus].resultAmount;
      draft.statusData.payments[calculateStatus] =
        initialState.statusData.payments[calculateStatus];
      break;
    }
    case RESET_STATUS: {
      const { statusName } = payload;
      draft.status[statusName] = initialState.status[statusName];
      break;
    }
    case RESET_FORM_ERROR: {
      const { field, form } = payload;
      draft.formErrors[form][field] = initialState.formErrors[form][field];
      draft.selectedAdjustmentFilter = form;
      break;
    }
    case RESET_STATE: {
      return initialState;
    }
    case RESET_STEP: {
      draft.step = initialState.step;
      break;
    }
    case SET_IS_SCHEDULE_DRAWER_OPEN: {
      const { isOpen } = payload;
      draft.isScheduleDrawerOpen = isOpen;
      break;
    }
    case SET_IS_SLIDER_FOCUSED: {
      const { form, value } = payload;
      draft.isSliderFocused[form] = value;
      break;
    }
    case UPDATE_FORM_ERROR: {
      const { field, form, value } = payload;
      draft.formErrors[form][field] = value;
      break;
    }
  }
}, initialState);

export default amortizationReducer;
